import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDropzone } from "react-dropzone";
import { Upload, FileSpreadsheet, X, AlertCircle } from "lucide-react";
import { LMSBASEURL } from "../config/apiUrls";
import PageHeader from "../components/PageHeader";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import ProgressModal from "../components/ProgressModal";
import Swal from "sweetalert2";
import { stream_data } from "../config/stream_data";

const POLL_INTERVAL = 4000;


const TpoFileUploader = () => {
  const navigate = useNavigate();
  const [selectedStream, setSelectedStream] = useState(null);
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [selectedSpecialization, setSelectedSpecialization] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [selectedStudyingYear, setSelectedStudyingYear] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [importProgress, setImportProgress] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [isRunningBackground,setisRunningBackground] = useState(false)

  const token = process.env.REACT_APP_API_TOKEN; 
  const AWS=process.env.REACT_APP_AWS_HOST
  const InstituteId = sessionStorage.getItem("instituteId");

  const availableYears = useMemo(() => {
      const years = new Set();
      const min = Math.min(...Array.from(years), new Date().getFullYear());
      const max = new Date().getFullYear() + 5;
      return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"], 
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      setErrors((prev) => ({ ...prev, file: null }));

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true});
        const jsonData = XLSX.utils
        .sheet_to_json(worksheet, { header: 1, raw: true })
        .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ""));
        function formatCell(cell, colIndex, headerRow) {
          const header = headerRow[colIndex]?.toLowerCase() || "";

          if (header === "date_of_birth" && typeof cell === "number") {
            const date = new Date(Math.round((cell - 25569) * 86400 * 1000));
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const dd = String(date.getDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
          }

          if (header === "contact_number") {
            return String(cell);
          }

          return cell;
        }

        const headerRow = jsonData[0];
        const formattedData = jsonData.map((row, rowIndex) => {
          if (rowIndex === 0) return row;
          return row.map((cell, colIndex) => formatCell(cell, colIndex, headerRow));
        });

        setPreviewData(
          formattedData.length > 51 ? formattedData.slice(0, 51) : formattedData
        );

        // setPreviewData(formattedData.slice(0, 51));
      };
      reader.readAsArrayBuffer(uploadedFile);
    },
  });

  const streamOptions = stream_data.streams.map((s) => ({ label: s.name, value: s.name }));
  
  const degreeOptions = useMemo(() => {
    const stream = stream_data.streams.find((s) => s.name === selectedStream?.value);
    return stream ? stream.degrees.map((d) => ({ label: d.name, value: d.name })) : [];
  }, [selectedStream]);
  
  const specializationOptions = useMemo(() => {
    const stream = stream_data.streams.find((s) => s.name === selectedStream?.value);
    const degree = stream?.degrees.find((d) => d.name === selectedDegree?.value);
    return degree ? degree.specializations.map((s) => ({ label: s, value: s })) : [];
  }, [selectedStream, selectedDegree]);
  
  const yearOptions = availableYears?.map((y) => ({ label: y, value: y }));
  const studyingYearOptions = stream_data.studying_years.map((y) => ({ label: y, value: y }));

  const validateForm = () => {
    let tempErrors = {};
    if (!selectedStream) tempErrors.stream = "Stream is required";
    if (!selectedDegree) tempErrors.degree = "Degree is required";
    if (!selectedYear) tempErrors.year = "Year is required";
    if (!selectedStudyingYear) tempErrors.studyingYear = "Studying Year is required";
    if (!file) tempErrors.file = "File is required";
    setErrors(tempErrors);
    setTouched({ stream: true, degree: true, year: true, studyingYear: true, file: true });
    return Object.keys(tempErrors).length === 0;
  };

  const handleUpload = async () => {
    if (!validateForm()) {
      return;
    }
    setUploading(true);

    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Accept", "application/json");

    const formdata = new FormData();
    formdata.append("institute_id", InstituteId);
    formdata.append("stream", selectedStream?.value);
    formdata.append("degree", selectedDegree?.value);
    formdata.append("specialization", selectedSpecialization?.value || "");
    formdata.append("year", selectedYear ? selectedYear.getFullYear() : "");
    formdata.append("studying_year", selectedStudyingYear?.value);
    formdata.append("file", file);

    try {
      const res = await fetch(`${LMSBASEURL}/api/tpo-import-students`, {
        method: "POST",
        headers: myHeaders,
        body: formdata,
      });

      if(res.ok){
        const result = await res.json();
        console.log("Upload Success:", result);
        if (result?.import_id) {
          setIsImporting(true);
          setShowProgressModal(true);
          setProgressMessage("Import in progress... please don't close or refresh this page.");
          pollImportProgress(result.import_id);
        } else {
          console.log("Upload completed but no import_id received.");
        }
        
        // Reset form
        setSelectedStream(null);
        setSelectedDegree(null);
        setSelectedSpecialization(null);
        setSelectedYear(null);
        setSelectedStudyingYear(null);
        setFile(null);
        setErrors({});
        setTouched({});
        setPreviewData([])
      }
    } catch (err) {
      console.error("Upload Error:", err);
      alert("❌ Upload failed!");
    } finally {
      setUploading(false);
    }
  };

  const pollImportProgress = async (importId) => {
    let intervalId;

    const poll = async () => {
      try {
        const res = await fetch(`${LMSBASEURL}/api/import-progress/${importId}`, {
          method: "GET",
          headers: {
            Authorization: token,
            Accept: "application/json",
          },
        });
        const result = await res.json();
        console.log("Progress:", result);

        if (result.success) {
          setImportProgress(result);
          if (result.status === "failed") {
            clearInterval(intervalId);
            setShowProgressModal(false)
            setIsImporting(false);
            setProgressMessage("❌ Import failed due to an error.");

            Swal.fire({
              icon: "error",
              title: "Import Failed",
              text: "Something went wrong during the import process.",
              confirmButtonColor: "#d33",
            });
            return;
          }
          if (result.total_rows === result.processed_rows) {
            setProgressMessage("✅ Import completed successfully!");
            setIsImporting(false);
            setShowProgressModal(false)
            clearInterval(intervalId);
            if(!isRunningBackground){
              Swal.fire({
                icon: "success",
                html: `
                  ✅ Import completed successfully!<br>
                  You will be automatically redirected to <b>Import Progress</b>.<br><br>
                  If not redirected automatically, 
                  <a href="/import-list?navigate=true" style="color:#3085d6; text-decoration:underline;">
                    click here
                  </a>.
                `,
                showConfirmButton: false,
                timer:3000,
                timerProgressBar:true,
                didClose: () => {
                  window.location.href = "/import-list?navigate=true";
                }
              });
            }
            setTimeout(() => {
              setShowProgressModal(false);
              navigate("/import-list?navigate=true");
              setisRunningBackground(false)
            }, 3500);
          }
        } else {
          console.warn("Polling failed:", result);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };

    // ✅ Immediately call once
    await poll();

    // ✅ Then start interval for subsequent polls
    intervalId = setInterval(poll, POLL_INTERVAL);
  };

  const removeFile = () => {
    setFile(null);
    setPreviewData([])
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused 
        ? '#0d6efd' 
        : errors[state.selectProps.name] && touched[state.selectProps.name]
        ? '#dc3545'
        : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#adb5bd'
      },
      minHeight: '38px',
      borderRadius: '0.375rem'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    })
  };

  const handleReset = async () => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Reset!",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) return;
    setSelectedStream(null);
    setSelectedDegree(null);
    setSelectedSpecialization(null);
    setSelectedYear(new Date());
    setSelectedStudyingYear(null);
    setFile(null);
    setErrors({});
    setTouched({});
    setPreviewData([])
  }

  const handleOnClose=()=>{
    setShowProgressModal(false)
    setisRunningBackground(true)
  }

  return (
    <div>
      <div className="col-12">
        <PageHeader title="Import Students" 
          breadcrumbs={[
            { label: "Dashboard", path: "/institute-dashboard" },
            { label: "Import Students ", active: true }
          ]}
        />
      </div>
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="card border rounded-4">
            <div className="card-header bg-gradient border-0 py-4" style={{
              
            }}>
              <p className="mb-0 text-dark fw-bold form-label">
                {/* <Upload className="me-2" size={28} style={{ verticalAlign: 'middle' }} /> */}
                Upload student data in bulk using Excel/CSV files
               </p>
              <p className="text-dark mb-0 mt-2"></p>
            </div>

            <div className="card-body">
              <div className="row g-2 mb-2">
                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold text-dark mb-2">
                    Stream <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="stream"
                    options={streamOptions}
                    value={selectedStream}
                    onChange={(val) => {
                      setSelectedStream(val);
                      setSelectedDegree(null);
                      setSelectedSpecialization(null);
                      setErrors((prev) => ({ ...prev, stream: null, degree: null }));
                      setTouched((prev) => ({ ...prev, stream: true }));
                    }}
                    placeholder="Select Stream"
                    styles={customSelectStyles}
                    isClearable
                    components={{ 
                      DropdownIndicator: () => null, 
                      IndicatorSeparator: () => null, 
                      MultiValue: ()=> null,
                    }}
                  />
                  {errors.stream && touched.stream && (
                    <div className="d-flex align-items-center mt-2 text-danger">
                      <AlertCircle size={14} className="me-1" />
                      <small>{errors.stream}</small>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold text-dark mb-2">
                    Degree <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="degree"
                    options={degreeOptions}
                    value={selectedDegree}
                    onChange={(val) => {
                      setSelectedDegree(val);
                      setSelectedSpecialization(null);
                      setErrors((prev) => ({ ...prev, degree: null }));
                      setTouched((prev) => ({ ...prev, degree: true }));
                    }}
                    placeholder={selectedStream ? "Select Degree" : "Select Stream first"}
                    isDisabled={!selectedStream}
                    styles={customSelectStyles}
                    isClearable
                    components={{ 
                      DropdownIndicator: () => null, 
                      IndicatorSeparator: () => null, 
                      MultiValue: ()=> null,
                    }}
                  />
                  {errors.degree && touched.degree && (
                    <div className="d-flex align-items-center mt-2 text-danger">
                      <AlertCircle size={14} className="me-1" />
                      <small>{errors.degree}</small>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold text-dark mb-2">
                    Specialization <span className="text-muted small">(Optional)</span>
                  </label>
                  <Select
                    name="specialization"
                    options={specializationOptions}
                    value={selectedSpecialization}
                    onChange={setSelectedSpecialization}
                    placeholder={selectedDegree ? "Select Specialization" : "Select Degree first"}
                    isDisabled={!selectedDegree}
                    styles={customSelectStyles}
                    isClearable
                    components={{ 
                      DropdownIndicator: () => null, 
                      IndicatorSeparator: () => null, 
                      MultiValue: ()=> null,
                    }}
                  />
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold text-dark mb-2">
                    Studying Year <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="studyingYear"
                    options={studyingYearOptions}
                    value={selectedStudyingYear}
                    onChange={(val) => {
                      setSelectedStudyingYear(val);
                      setErrors((prev) => ({ ...prev, studyingYear: null }));
                      setTouched((prev) => ({ ...prev, studyingYear: true }));
                    }}
                    placeholder="Select Studying Year"
                    styles={customSelectStyles}
                    isClearable
                    components={{ 
                      DropdownIndicator: () => null, 
                      IndicatorSeparator: () => null, 
                      MultiValue: ()=> null,
                    }}
                  />
                  {errors.studyingYear && touched.studyingYear && (
                    <div className="d-flex align-items-center mt-2 text-danger">
                      <AlertCircle size={14} className="me-1" />
                      <small>{errors.studyingYear}</small>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold text-dark mb-2">
                    Year <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative">
                    <DatePicker
                      selected={selectedYear}
                      onChange={(val) => {
                        setSelectedYear(val);
                        setErrors((prev) => ({ ...prev, year: null }));
                        setTouched((prev) => ({ ...prev, year: true }));
                      }}
                      showYearPicker
                      dateFormat="yyyy"
                      yearItemNumber={9}
                      placeholderText="Select Year"
                      className="form-control"
                      isClearable
                      wrapperClassName="d-block"
                      maxDate={new Date()}
                    />
                  </div>
                  {errors.year && touched.year && (
                    <div className="d-flex align-items-center mt-2 text-danger">
                      <AlertCircle size={14} className="me-1" />
                      <small>{errors.year}</small>
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-1">
                  <p className=" form-label fw-semibold text-dark mb-2">
                   Reset
                  </p>

                  <button
                    className="btn btn-danger w-75 rounded-3"
                    onClick={handleReset}
                    disabled={
                      !selectedStream && 
                      !selectedDegree && 
                      !selectedSpecialization && 
                      !selectedYear && 
                      !selectedStudyingYear && 
                      !file
                    }
                  >
                    <X size={16} className="" style={{ verticalAlign: 'middle' }} />
                    
                  </button>
                </div>

              </div>

              {isImporting ||<div className="mb-4 mt-4">
                <div className="d-flex justify-content-between">
                  <label className="form-label fw-semibold text-dark mb-3">
                    Upload File <span className="text-danger">*</span>
                  </label>
                  <div className="text-right">
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: "#36348e", color: "white" }}
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = `${AWS}uploads/sample/revised sample file student import.xlsx`;
                        link.download = "imports_student.xlsx";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <i className="ri-download-2-line me-1"></i>
                      Sample XLSX format
                    </button>
                  </div>

                </div>
                
                {!file ? (
                  <div
                    {...getRootProps()}
                    className={`border border-2 rounded-4 p-5 text-center cursor-pointer transition-all ${
                      isDragActive 
                        ? 'border-primary bg-primary bg-opacity-10' 
                        : errors.file && touched.file
                        ? 'border-danger bg-danger bg-opacity-10'
                        : 'border-dashed'
                    }`}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderStyle: 'dashed'
                    }}
                  >
                    <input {...getInputProps()} />
                    <div className="mb-3">
                      <Upload 
                        size={48} 
                        className={isDragActive ? 'text-primary' : 'text-muted'} 
                        strokeWidth={1.5}
                      />
                    </div>
                    {isDragActive ? (
                      <div>
                        <h5 className="fw-bold text-primary mb-2">Drop your file here</h5>
                        <p className="text-muted mb-0">Release to upload</p>
                      </div>
                    ) : (
                      <div>
                        <h5 className="fw-bold text-dark mb-2">Drag & drop your Excel file here</h5>
                        <p className="text-muted mb-3">
                          or <span className="text-primary fw-semibold">click to browse</span>
                        </p>
                        <div className="d-inline-block px-3 py-2 bg-light rounded-3">
                          <small className="text-muted">
                            <FileSpreadsheet size={16} className="me-1" style={{ verticalAlign: 'middle' }} />
                            Accepted format: <strong>.xlsx and .csv</strong>
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border border-2 border-success rounded-4 p-4 bg-success bg-opacity-10">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="bg-success bg-opacity-25 rounded-3 p-3 me-3">
                          <FileSpreadsheet size={32} className="text-success" />
                        </div>
                        <div>
                          <h6 className="fw-bold mb-1 text-dark">{file.name}</h6>
                          <small className="text-muted">
                            {(file.size / 1024).toFixed(2)} KB • Ready to upload
                          </small>
                        </div>
                      </div>
                      <button
                        className="btn btn-outline-danger btn-sm rounded-circle"
                        onClick={removeFile}
                        style={{ width: '36px', height: '36px' }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-top">
                      <div {...getRootProps()} style={{ cursor: 'pointer' }}>
                        <input {...getInputProps()} />
                        <small className="text-primary fw-semibold">
                          <Upload size={14} className="me-1" style={{ verticalAlign: 'middle' }} />
                          Click to replace file
                        </small>
                      </div>
                    </div>
                  </div>
                )}
                
                {errors.file && touched.file && (
                  <div className="d-flex align-items-center mt-2 text-danger">
                    <AlertCircle size={14} className="me-1" />
                    <small>{errors.file}</small>
                  </div>
                )}
              </div>}

              {previewData.length > 0 && (
                <>
                <h5 className="text-dark mt-2">Preview Data It's only show first 50 data</h5>
                <div className="table-responsive mt-3 mb-4">
                  <table className="table table-bordered table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{whiteSpace: "nowrap",}}>Sr. No</th>
                        {previewData[0].map((col, idx) => (
                          <th key={idx} className="text-capitalize " style={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis", 
                            overflow:"hidden"
                          }}>{col?.replace(/_/g, " ") || `Column ${idx + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(1).map((row, i) => (
                        <tr key={i} style={{whiteSpace: "nowrap",}}>
                          <td>{i+1}</td>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
              <div className="d-flex justify-content-end gap-3">
                <button
                  className="btn btn-lg btn-primary px-5 rounded-3 shadow-sm"
                  onClick={handleUpload}
                  disabled={uploading ||importProgress || isImporting}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  {uploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                      Upload File
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showProgressModal && (
        <ProgressModal
          show={showProgressModal}
          importProgress={importProgress}
          onClose={handleOnClose}
        />
      )}

      {/* <ProgressModal
        show={true}
        importProgress={{
          "success": true,
          "import_id": "64ca7771-4629-4382-b5d5-211f4e9fc730",
          "total_rows": 350,
          "processed_rows": 50,
          "success_count": 318,
          "duplicate_count": 12,
          "error_count": 0,
          "batch_logs": [
              {
                  "errors": 0,
                  "success": 338,
                  "duplicates": 12,
                  "batch_number": 2,
                  "rows_in_batch": 150
              }
          ]
        }}
        onClose={() => setShowProgressModal(false)}
      /> */}
    </div>
  );
};

export default TpoFileUploader;